import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { drw, errText } from "./api";

const COLS = ["sheet_number", "layer", "author", "type", "subject", "label", "room_number", "comments", "quantity", "status", "created_at"];
const HEAD = { sheet_number: "Sheet", layer: "Layer", author: "Author", type: "Type", subject: "Subject", label: "Label",
  room_number: "Room", comments: "Comments", quantity: "Qty", status: "Status", created_at: "Date" };

/** The imported Bluebeam summary with zero re-entry: every markup a row, filterable, click-to-jump. */
export default function MarkupList({ projectId, markups, onJump, onChanged, sheetScope }) {
  const [f, setF] = useState({ layer: "", type: "", status: "", author: "", q: "" });
  const [assigning, setAssigning] = useState(null);

  const opts = (k) => [...new Set(markups.map((m) => m[k]).filter(Boolean))].sort();
  const rows = useMemo(() => markups.filter((m) =>
    (!f.layer || m.layer === f.layer) && (!f.type || m.type === f.type) && (!f.status || m.status === f.status)
    && (!f.author || m.author === f.author)
    && (!f.q || [m.subject, m.label, m.comments, m.space_name, m.room_ref?.room_number].some((v) => String(v || "").toLowerCase().includes(f.q.toLowerCase())))),
  [markups, f]);

  const setStatus = async (m, status) => {
    if (m.pending) { toast.message("Waiting to sync — change it once it lands"); return; }
    try {
      const { queued } = await drw.patch(`/markups/${m.id}`, { status }, { _project_id: projectId });
      if (queued) toast.message("Saved on this device — syncs when signal returns");
      onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };
  const assign = async (m) => {
    const to = assigning?.value ?? "";
    if (m.pending) { toast.message("Waiting to sync — assign it once it lands"); return; }
    try {
      const { queued } = await drw.patch(`/markups/${m.id}`, { assigned_to: to }, { _project_id: projectId });
      if (queued) toast.message("Saved on this device — syncs when signal returns");
      setAssigning(null);
      onChanged?.();
    } catch (e) { toast.error(errText(e)); }
  };
  const csv = () => {
    const token = localStorage.getItem("auth_token");
    drw.get(`/projects/${projectId}/markups`, { params: { fmt: "csv", sheet_id: sheetScope || "" }, responseType: "blob",
      headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "markups.csv"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 3000); })
      .catch((e) => toast.error(errText(e)));
  };

  return (
    <div className="flex flex-col h-full" data-testid="markup-list">
      <div className="flex flex-wrap items-center gap-1.5 p-2 border-b border-neutral-200 bg-white sticky top-0 z-10">
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search..."
               className="h-8 text-xs border rounded-md px-2 w-44" data-testid="markup-list-search" />
        {["layer", "type", "status", "author"].map((k) => (
          <select key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-8 text-xs border rounded-md px-1"
                  data-testid={`markup-list-filter-${k}`}>
            <option value="">{HEAD[k] || k}</option>
            {opts(k).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <span className="text-xs text-neutral-500 ml-auto" data-testid="markup-list-count">{rows.length} of {markups.length}</span>
        <button type="button" onClick={csv} className="h-8 px-2 text-xs border rounded-md flex items-center gap-1 hover:bg-neutral-50" data-testid="markup-list-csv">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      </div>
      <div className="overflow-auto flex-1">
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 text-neutral-600 sticky top-0">
            <tr>{COLS.map((c) => <th key={c} className="text-left font-semibold px-2 py-1.5 whitespace-nowrap">{HEAD[c]}</th>)}<th /></tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-neutral-100 hover:bg-orange-50 cursor-pointer" onClick={() => onJump(m)}
                  data-testid={`markup-row-${m.id}`}>
                {COLS.map((c) => (
                  <td key={c} className="px-2 py-1.5 max-w-[14rem] truncate">
                    {c === "created_at" ? (m[c] || "").slice(0, 10)
                      : c === "quantity" ? (m.type === "count" || m.type === "symbol" ? 1 : "")
                        : c === "room_number" ? (m.room_ref?.room_number || m.space_name || "")
                          : c === "status" ? <span className={`px-1.5 py-0.5 rounded ${m.status === "resolved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>{m.status}{m.verify_state === "verify" ? " · verify" : ""}</span>
                            : String(m[c] ?? "")}
                  </td>
                ))}
                <td className="px-2 py-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="text-orange-700 hover:underline mr-2" onClick={() => setStatus(m, m.status === "open" ? "resolved" : "open")}
                          data-testid={`markup-row-toggle-${m.id}`}>{m.status === "open" ? "Resolve" : "Reopen"}</button>
                  {assigning?.id === m.id ? (
                    <span className="inline-flex gap-1">
                      <input autoFocus value={assigning.value} onChange={(e) => setAssigning({ id: m.id, value: e.target.value })} placeholder="email"
                             className="h-6 border rounded px-1 w-32" data-testid={`markup-row-assign-input-${m.id}`} />
                      <button type="button" className="text-blue-700" onClick={() => assign(m)} data-testid={`markup-row-assign-save-${m.id}`}>Save</button>
                    </span>
                  ) : (
                    <button type="button" className="text-blue-700 hover:underline" onClick={() => setAssigning({ id: m.id, value: m.assigned_to || "" })}
                            data-testid={`markup-row-assign-${m.id}`}>{m.assigned_to ? `→ ${m.assigned_to.split("@")[0]}` : "Assign"}</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={COLS.length + 1} className="px-3 py-6 text-center text-neutral-400">No markups match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
